import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import {
  ChefHat,
  ShoppingCart,
  Sparkles,
  Plus,
  Trash2,
  Calendar as CalIcon,
  Phone,
  Copy,
  Plane,
  Wine,
  PartyPopper,
  Salad,
  Utensils,
  ShieldCheck,
} from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";

/* ------------------------------------------------------------------ */
/* Service catalog (mirrors ADD_ON_PRICING on the backend)             */
/* ------------------------------------------------------------------ */
const SERVICES = [
  { key: "chef", icon: ChefHat, label: "Private Chef", priceLabel: "$200 / hour", needs: "hours" },
  { key: "grocery", icon: ShoppingCart, label: "Grocery Stocking", priceLabel: "$75 + cost of groceries", needs: "list" },
  { key: "housekeeping", icon: Sparkles, label: "Daily Housekeeping", priceLabel: "$275 / day", needs: "days" },
  { key: "prepared_meals", icon: Salad, label: "Pre-Made Local Cuisine", priceLabel: "$150 / person · per meal", needs: "meals" },
  { key: "meal_service", icon: Utensils, label: "Meal Services (B/L/D)", priceLabel: "$225 / person / day", needs: "service_days" },
  { key: "airport_transport", icon: Plane, label: "Airport Transportation", priceLabel: "Concierge priced", needs: null },
  { key: "bar_service", icon: Wine, label: "Prepared Bar Services", priceLabel: "Concierge priced", needs: null },
  { key: "event_planning", icon: PartyPopper, label: "Event Planning", priceLabel: "Concierge priced", needs: null },
  { key: "all_inclusive", icon: ShieldCheck, label: "All Inclusive", priceLabel: "Concierge priced", needs: null },
];

const todayISO = () => new Date().toISOString().slice(0, 10);
const plusDaysISO = (d) =>
  new Date(Date.now() + d * 86400000).toISOString().slice(0, 10);

export default function AddOnsPage() {
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const refFromUrl = searchParams.get("ref") || "";
  const navigate = useNavigate();

  const [form, setForm] = useState({
    name: user?.name || "",
    email: user?.email || "",
    phone: "",
    trip_ref: refFromUrl,
    start_date: todayISO(),
    end_date: plusDaysISO(5),
    party_size: 2,
    services: [],
    chef_hours: 4,
    housekeeping_days: 0,
    prepared_meals_count: 0,
    meal_service_days: 0,
    notes: "",
    marketing_pref: "all_communications",
    // Property location & owner — required for service delivery (groceries,
    // housekeeping, etc. all need an address + somebody to coordinate with).
    property_address: "",
    property_city: "",
    property_country: "",
    property_owner_name: "",
    property_owner_email: "",
    property_owner_phone: "",
  });
  const [groceryRows, setGroceryRows] = useState([
    { name: "", qty: "", notes: "" },
  ]);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null); // {id, estimate_usd, ...}

  // Auto-derive housekeeping/meal-service days when dates change and the user
  // hasn't manually overridden them
  useEffect(() => {
    const days = Math.max(
      0,
      Math.round(
        (new Date(form.end_date) - new Date(form.start_date)) / 86400000,
      ),
    );
    setForm((f) => ({
      ...f,
      housekeeping_days: f.services.includes("housekeeping") ? days : f.housekeeping_days,
      meal_service_days: f.services.includes("meal_service") ? days : f.meal_service_days,
    }));
  }, [form.start_date, form.end_date, form.services]);

  const toggleService = (key) => {
    setForm((f) => ({
      ...f,
      services: f.services.includes(key)
        ? f.services.filter((s) => s !== key)
        : [...f.services, key],
    }));
  };

  const updateGrocery = (i, patch) =>
    setGroceryRows((rows) => rows.map((r, idx) => (idx === i ? { ...r, ...patch } : r)));
  const addGroceryRow = () =>
    setGroceryRows((rows) => [...rows, { name: "", qty: "", notes: "" }]);
  const removeGroceryRow = (i) =>
    setGroceryRows((rows) => rows.filter((_, idx) => idx !== i));

  const partySize = Math.max(1, parseInt(form.party_size || "1", 10) || 1);

  const estimate = useMemo(() => {
    let total = 0;
    if (form.services.includes("chef")) total += (parseFloat(form.chef_hours) || 0) * 200;
    if (form.services.includes("grocery")) total += 75;
    if (form.services.includes("housekeeping"))
      total += (parseInt(form.housekeeping_days, 10) || 0) * 275;
    if (form.services.includes("prepared_meals"))
      total += (parseInt(form.prepared_meals_count, 10) || 0) * 150 * partySize;
    if (form.services.includes("meal_service"))
      total += (parseInt(form.meal_service_days, 10) || 0) * 225 * partySize;
    return Math.round(total);
  }, [form, partySize]);

  const submit = async (e) => {
    e.preventDefault();
    if (form.services.length === 0) {
      toast.error("Pick at least one service.");
      return;
    }
    setBusy(true);
    try {
      const payload = {
        ...form,
        party_size: partySize,
        chef_hours: parseFloat(form.chef_hours) || 0,
        housekeeping_days: parseInt(form.housekeeping_days, 10) || 0,
        prepared_meals_count: parseInt(form.prepared_meals_count, 10) || 0,
        meal_service_days: parseInt(form.meal_service_days, 10) || 0,
        grocery_list: form.services.includes("grocery")
          ? groceryRows.filter((g) => g.name.trim()).map((g) => ({
              name: g.name.trim(),
              qty: g.qty.trim() || null,
              notes: g.notes.trim() || null,
            }))
          : [],
      };
      const r = await api.post("/add-ons/request", payload);
      setDone(r.data);
      toast.success("Request submitted — concierge has it.");
    } catch (err) {
      console.error("Add-ons submit failed", err);
      toast.error(err?.response?.data?.detail || "Could not send — please try again.");
    } finally {
      setBusy(false);
    }
  };

  if (done) {
    return (
      <div className="max-w-2xl mx-auto px-6 py-20" data-testid="add-ons-success">
        <div className="text-center">
          <Sparkles size={28} strokeWidth={1.25} className="text-[var(--gold)] mx-auto" />
          <div className="overline mt-4">Submitted</div>
          <h1 className="font-display text-5xl text-[var(--emerald)] mt-3">
            Your add-ons are with us.
          </h1>
          <p className="text-[var(--ink-2)] mt-4">
            Our concierge will reach out to confirm timing and final pricing.
          </p>
        </div>

        <div className="lux-card-dark mt-10 !p-7">
          <div className="overline text-[var(--gold)]">Estimate</div>
          <div className="font-display text-4xl mt-2 tabular-nums">
            ${done.estimate_usd?.toLocaleString() || 0}
          </div>
          <p className="text-sm opacity-90 mt-3">
            Concierge-priced services (transport, bar, event planning, all-inclusive) finalised by your concierge.
          </p>
          {form.trip_ref && (
            <div className="mt-5 flex items-center gap-3">
              <div className="overline text-[var(--gold)]">Trip reference</div>
              <div className="font-display text-2xl tracking-wide">{form.trip_ref}</div>
              <button
                onClick={() => {
                  navigator.clipboard?.writeText(form.trip_ref);
                  toast.success("Copied.");
                }}
                className="btn-gold !py-2 !px-3 text-[10px]"
              >
                <Copy size={12} strokeWidth={1.5} /> Copy
              </button>
            </div>
          )}
        </div>

        <div className="grid sm:grid-cols-2 gap-3 mt-6">
          <a
            href={`tel:${done.concierge_phone}`}
            className="btn-primary w-full"
            data-testid="add-ons-call-btn"
          >
            <Phone size={14} strokeWidth={1.5} /> Call concierge
          </a>
          <button
            onClick={() => navigate("/")}
            className="btn-ghost w-full"
            data-testid="add-ons-back-btn"
          >
            Back to Sasha
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-5xl mx-auto px-6 lg:px-12 py-16 bg-white" data-testid="jt-essentials-page">
      <div className="overline text-[var(--brand-red)]">JTC Perks · concierge-curated</div>
      <h1 className="font-display text-5xl sm:text-6xl text-[var(--ink)] mt-3 leading-[1.05]">
        Inclusive moments,<br />
        <em className="not-italic text-[var(--brand-red)]">curated</em> end to end.
      </h1>
      <p className="text-[var(--ink-2)] mt-5 max-w-2xl">
        Layer chef nights, fully stocked kitchens, daily housekeeping, prepared meals, airport
        transport, bar services and full event planning onto any Janté stay.
      </p>

      <div
        className="mt-7 px-5 py-4 border-l-2 border-[var(--brand-red)] bg-[var(--brand-red)]/6 text-sm text-[var(--ink-2)] leading-relaxed max-w-2xl"
        data-testid="jt-essentials-disclaimer"
      >
        <strong className="text-[var(--brand-red)]">Estimate, not yet anchored.</strong>{" "}
        JTC Perks shown here are estimates. Once your trip details are
        finalized, your perk pricing will be anchored and locked in.
      </div>

      <form onSubmit={submit} className="mt-12 space-y-10">
        {/* Trip reference + contact */}
        <div className="lux-card grid sm:grid-cols-2 gap-6">
          {form.trip_ref && (
            <div className="sm:col-span-2 flex items-center gap-3 flex-wrap pb-3 border-b border-[var(--emerald)]/10">
              <div className="overline">Trip reference</div>
              <div
                className="font-display text-2xl text-[var(--emerald)] tracking-wide"
                data-testid="add-ons-trip-ref"
              >
                {form.trip_ref}
              </div>
              <span className="text-[11px] text-[var(--muted)]">
                Auto-attached — Sasha forwards your contact details + this reference to the concierge team.
              </span>
            </div>
          )}
          <div>
            <div className="overline mb-1">Name</div>
            <input
              className="lux-input"
              required
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              data-testid="add-ons-name"
            />
          </div>
          <div>
            <div className="overline mb-1">Email</div>
            <input
              className="lux-input"
              type="email"
              required
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              data-testid="add-ons-email"
            />
          </div>
          <div>
            <div className="overline mb-1">Phone</div>
            <input
              className="lux-input"
              required
              value={form.phone}
              onChange={(e) => setForm({ ...form, phone: e.target.value })}
              placeholder="+1 (315) 897-5589"
              data-testid="add-ons-phone"
            />
          </div>
          <div>
            <div className="overline mb-1">Communication preference</div>
            <select
              className="lux-input"
              value={form.marketing_pref}
              onChange={(e) => setForm({ ...form, marketing_pref: e.target.value })}
              data-testid="add-ons-marketing-pref"
            >
              <option value="all_communications">All communications</option>
              <option value="vacation_only">Vacation Reference Only</option>
              <option value="no_marketing">No Marketing</option>
            </select>
          </div>
        </div>

        {/* Travel dates + party */}
        <div className="lux-card grid sm:grid-cols-3 gap-6 items-end">
          <div>
            <div className="overline mb-1 flex items-center gap-1.5">
              <CalIcon size={11} strokeWidth={1.5} className="text-[var(--gold)]" /> Arrival
            </div>
            <input
              type="date"
              className="lux-input"
              value={form.start_date}
              onChange={(e) => setForm({ ...form, start_date: e.target.value })}
              data-testid="add-ons-start"
            />
          </div>
          <div>
            <div className="overline mb-1 flex items-center gap-1.5">
              <CalIcon size={11} strokeWidth={1.5} className="text-[var(--gold)]" /> Departure
            </div>
            <input
              type="date"
              className="lux-input"
              value={form.end_date}
              onChange={(e) => setForm({ ...form, end_date: e.target.value })}
              data-testid="add-ons-end"
            />
          </div>
          <div>
            <div className="overline mb-1">Party size</div>
            <input
              type="number"
              min="1"
              className="lux-input"
              value={form.party_size}
              onChange={(e) => setForm({ ...form, party_size: e.target.value })}
              data-testid="add-ons-party"
            />
          </div>
        </div>

        {/* Property location + owner contact — needed so the concierge can
            coordinate deliveries, housekeeping access, and on-site contact
            for the property. */}
        <div className="lux-card space-y-6" data-testid="jt-essentials-property-block">
          <div>
            <div className="overline text-[var(--brand-red)]">Where is the property?</div>
            <p className="text-xs text-[var(--ink-2)] mt-1">
              So our concierge knows where to send chefs, groceries, and housekeeping.
            </p>
          </div>
          <div className="grid sm:grid-cols-2 gap-6">
            <div className="sm:col-span-2">
              <div className="overline mb-1">Property address *</div>
              <input
                type="text"
                required
                className="lux-input"
                value={form.property_address}
                onChange={(e) => setForm({ ...form, property_address: e.target.value })}
                placeholder="Street address, unit / villa number"
                data-testid="add-ons-property-address"
              />
            </div>
            <div>
              <div className="overline mb-1">City *</div>
              <input
                type="text"
                required
                className="lux-input"
                value={form.property_city}
                onChange={(e) => setForm({ ...form, property_city: e.target.value })}
                data-testid="add-ons-property-city"
              />
            </div>
            <div>
              <div className="overline mb-1">Country</div>
              <input
                type="text"
                className="lux-input"
                value={form.property_country}
                onChange={(e) => setForm({ ...form, property_country: e.target.value })}
                data-testid="add-ons-property-country"
              />
            </div>
          </div>

          <div className="pt-2 border-t border-[var(--emerald)]/10">
            <div className="overline text-[var(--brand-red)]">Property owner / on-site contact</div>
            <p className="text-xs text-[var(--ink-2)] mt-1">
              Who should the concierge coordinate with for access and timing?
            </p>
          </div>
          <div className="grid sm:grid-cols-3 gap-6">
            <div>
              <div className="overline mb-1">Name</div>
              <input
                type="text"
                className="lux-input"
                value={form.property_owner_name}
                onChange={(e) => setForm({ ...form, property_owner_name: e.target.value })}
                data-testid="add-ons-owner-name"
              />
            </div>
            <div>
              <div className="overline mb-1">Email</div>
              <input
                type="email"
                className="lux-input"
                value={form.property_owner_email}
                onChange={(e) => setForm({ ...form, property_owner_email: e.target.value })}
                data-testid="add-ons-owner-email"
              />
            </div>
            <div>
              <div className="overline mb-1">Phone</div>
              <input
                type="tel"
                className="lux-input"
                value={form.property_owner_phone}
                onChange={(e) => setForm({ ...form, property_owner_phone: e.target.value })}
                placeholder="+1 (315) 897-5589"
                data-testid="add-ons-owner-phone"
              />
            </div>
          </div>
        </div>

        {/* Services */}
        <div>
          <div className="overline">Choose your JTC Perks</div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 mt-5">
            {SERVICES.map((s) => {
              const selected = form.services.includes(s.key);
              return (
                <button
                  type="button"
                  key={s.key}
                  onClick={() => toggleService(s.key)}
                  className={`lux-card text-left transition-all ${
                    selected
                      ? "border-[var(--emerald)] ring-2 ring-[var(--gold)]"
                      : "hover:-translate-y-0.5"
                  }`}
                  data-testid={`add-on-${s.key}`}
                >
                  <s.icon
                    size={20}
                    strokeWidth={1.25}
                    className="text-[var(--gold)]"
                  />
                  <div className="font-display text-2xl text-[var(--emerald)] mt-3">
                    {s.label}
                  </div>
                  <div className="text-[11px] uppercase tracking-[0.18em] text-[var(--brand-red)] mt-1.5">
                    {s.priceLabel}
                  </div>
                  <div
                    className={`text-[10px] uppercase tracking-[0.18em] mt-3 ${
                      selected ? "text-[var(--emerald)]" : "text-[var(--muted)]"
                    }`}
                  >
                    {selected ? "Added · tap to remove" : "Tap to add"}
                  </div>
                </button>
              );
            })}
          </div>
        </div>

        {/* Per-service quantities */}
        {(form.services.includes("chef") ||
          form.services.includes("housekeeping") ||
          form.services.includes("prepared_meals") ||
          form.services.includes("meal_service")) && (
          <div className="lux-card grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {form.services.includes("chef") && (
              <div>
                <div className="overline mb-1">Chef hours</div>
                <input
                  type="number"
                  min="0"
                  step="0.5"
                  className="lux-input"
                  value={form.chef_hours}
                  onChange={(e) => setForm({ ...form, chef_hours: e.target.value })}
                  data-testid="add-on-chef-hours"
                />
              </div>
            )}
            {form.services.includes("housekeeping") && (
              <div>
                <div className="overline mb-1">Housekeeping days</div>
                <input
                  type="number"
                  min="0"
                  className="lux-input"
                  value={form.housekeeping_days}
                  onChange={(e) =>
                    setForm({ ...form, housekeeping_days: e.target.value })
                  }
                  data-testid="add-on-hk-days"
                />
              </div>
            )}
            {form.services.includes("prepared_meals") && (
              <div>
                <div className="overline mb-1">Prepared meals (count)</div>
                <input
                  type="number"
                  min="0"
                  className="lux-input"
                  value={form.prepared_meals_count}
                  onChange={(e) =>
                    setForm({ ...form, prepared_meals_count: e.target.value })
                  }
                  data-testid="add-on-meals-count"
                />
              </div>
            )}
            {form.services.includes("meal_service") && (
              <div>
                <div className="overline mb-1">Meal-service days</div>
                <input
                  type="number"
                  min="0"
                  className="lux-input"
                  value={form.meal_service_days}
                  onChange={(e) =>
                    setForm({ ...form, meal_service_days: e.target.value })
                  }
                  data-testid="add-on-meal-days"
                />
              </div>
            )}
          </div>
        )}

        {/* Grocery list (only when grocery service selected) */}
        {form.services.includes("grocery") && (
          <div className="lux-card" data-testid="grocery-list">
            <div className="flex items-center justify-between flex-wrap gap-3">
              <div>
                <div className="overline">Fillable grocery list</div>
                <div className="font-display text-2xl text-[var(--emerald)] mt-1">
                  Stock the kitchen
                </div>
              </div>
              <button
                type="button"
                onClick={addGroceryRow}
                className="btn-ghost text-[11px]"
                data-testid="grocery-add-row"
              >
                <Plus size={12} strokeWidth={1.5} /> Add item
              </button>
            </div>
            <div className="mt-5 space-y-3">
              {groceryRows.map((g, i) => (
                <div
                  key={g.id || `grocery-${i}`}
                  className="grid grid-cols-[1.5fr_0.7fr_2fr_auto] gap-3 items-center"
                >
                  <input
                    placeholder="Item (e.g. burrata)"
                    className="lux-input"
                    value={g.name}
                    onChange={(e) => updateGrocery(i, { name: e.target.value })}
                    data-testid={`grocery-name-${i}`}
                  />
                  <input
                    placeholder="Qty"
                    className="lux-input"
                    value={g.qty}
                    onChange={(e) => updateGrocery(i, { qty: e.target.value })}
                    data-testid={`grocery-qty-${i}`}
                  />
                  <input
                    placeholder="Notes (brand, ripeness, etc.)"
                    className="lux-input"
                    value={g.notes}
                    onChange={(e) => updateGrocery(i, { notes: e.target.value })}
                    data-testid={`grocery-notes-${i}`}
                  />
                  {groceryRows.length > 1 && (
                    <button
                      type="button"
                      onClick={() => removeGroceryRow(i)}
                      className="btn-ghost text-[var(--burgundy)]"
                      aria-label="Remove"
                    >
                      <Trash2 size={13} strokeWidth={1.25} />
                    </button>
                  )}
                </div>
              ))}
            </div>
            <p className="text-[11px] text-[var(--muted)] mt-4">
              Groceries billed at cost in addition to the $75 delivery fee.
            </p>
          </div>
        )}

        {/* Notes + estimate */}
        <div className="grid lg:grid-cols-[2fr_1fr] gap-6 items-stretch">
          <div className="lux-card">
            <div className="overline mb-2">Anything Sasha should know?</div>
            <textarea
              className="lux-input min-h-[120px]"
              value={form.notes}
              onChange={(e) => setForm({ ...form, notes: e.target.value })}
              placeholder="Allergies, dietary preferences, must-have brands, dream meals…"
              data-testid="add-ons-notes"
            />
          </div>
          <div className="lux-card-dark flex flex-col">
            <div className="overline text-[var(--gold)]">Live estimate</div>
            <div className="font-display text-5xl mt-2 tabular-nums" data-testid="add-ons-estimate">
              ${estimate.toLocaleString()}
            </div>
            <p className="text-xs opacity-80 mt-3">
              Excludes concierge-priced services (transport, bar, event planning, all-inclusive) — those
              firmed up by your concierge.
            </p>
          </div>
        </div>

        <div className="flex flex-wrap gap-3">
          <button type="submit" disabled={busy} className="btn-primary" data-testid="add-ons-submit">
            {busy ? "Sending…" : "Send to concierge"}
          </button>
          <button type="button" onClick={() => navigate(-1)} className="btn-ghost">
            Cancel
          </button>
        </div>
      </form>
    </div>
  );
}
